import { Box, Dialog, DialogActions, DialogContent, DialogTitle, styled } from '@mui/material';
import { Button as V3Button } from 'components/Ui-V3/Button/Button';
import { Area } from 'react-easy-crop';
import { AspectOption, ImageEditor } from './ImageEditor';

const StyledDialogTitle = styled(DialogTitle)({
    fontSize: '1.25rem',
    fontWeight: 600,
    padding: '24px 24px 16px',
});

const StyledDialogContent = styled(DialogContent)({
    padding: '0 24px 24px',
    overflowY: 'auto',
    overflowX: 'hidden',
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
});

const StyledDialogActions = styled(DialogActions)({
    padding: '16px 24px 24px',
    gap: '12px',
    flexShrink: 0,
});

const EditorContainer = styled(Box)({
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
});

type ImageEditorModalProps = {
    open: boolean;
    image: { url: string; title: string };
    rotation: number;
    isLoading: boolean;
    isCropReady: boolean;
    title?: string;
    aspect?: number;
    aspectOptions?: AspectOption[];
    backgroundColor?: string;
    onClose: () => void;
    onConfirm: () => void;
    setRotation: (rotation: number) => void;
    setCroppedAreaPixels: (pixels: Area | null) => void;
};

export const ImageEditorModal = ({
    open,
    image,
    rotation,
    isLoading,
    isCropReady,
    title = 'Adjust Image',
    aspect,
    aspectOptions,
    backgroundColor,
    onClose,
    onConfirm,
    setRotation,
    setCroppedAreaPixels,
}: ImageEditorModalProps) => {
    const handleClose = () => {
        if (!isLoading) {
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: '12px',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                },
            }}
        >
            <StyledDialogTitle>{title}</StyledDialogTitle>
            <StyledDialogContent>
                <EditorContainer>
                    <ImageEditor
                        image={image}
                        rotation={rotation}
                        aspect={aspect}
                        aspectOptions={aspectOptions}
                        backgroundColor={backgroundColor}
                        setRotation={setRotation}
                        setCroppedAreaPixels={setCroppedAreaPixels}
                    />
                </EditorContainer>
            </StyledDialogContent>
            <StyledDialogActions>
                <V3Button variant="outline" size="small" onClick={handleClose} disabled={isLoading} fullWidth>
                    Cancel
                </V3Button>
                <V3Button
                    size="small"
                    onClick={onConfirm}
                    loading={isLoading}
                    disabled={!isCropReady || isLoading}
                    fullWidth
                >
                    Apply
                </V3Button>
            </StyledDialogActions>
        </Dialog>
    );
};
